import styled from "@emotion/styled";
import React from "react";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { AppCard, AppCardContent, AppCardFooter } from "../Generic/AppCard";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import SafeArea from "../Generic/SafeArea";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";

type Props = {
  onClickGoBack: () => void;
};

export default function CareMomentsExplainedScreen(props: Props) {
  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.HOW_CARE_MOMENTS_WORKS)} />}
      onClickGoBack={props.onClickGoBack}
    >
      <Steps>
        <Step
          title={translate(STRINGS.FIND_A_CARE_MOMENT)}
          description={translate(STRINGS.FIND_A_CARE_MOMENT_DESCRIPTION)}
          imgSrc="/assets/3d-ramp-trophy.jpg"
        />

        <Step
          direction="reverse"
          title={translate(STRINGS.VISIT_COUNTS)}
          description={translate(STRINGS.VISIT_COUNTS_DESCRIPTION)}
          imgSrc="/assets/3d-torso-share.jpg"
          animationDelay={100}
        />

        <Step
          title={translate(STRINGS.STAY_ON_TRACK)}
          description={translate(STRINGS.STAY_ON_TRACK_DESCRIPTION)}
          imgSrc="/assets/3d-ramp-marker.jpg"
          footer={
            <SubStepRoot>
              <StepContent>
                <StepTitle>{translate(STRINGS.STAY_ON_TRACK_1_QUESTION)}</StepTitle>
                <StepDescription>{translate(STRINGS.STAY_ON_TRACK_1_ANSWER)}</StepDescription>
              </StepContent>

              <StepContent>
                <StepTitle>{translate(STRINGS.STAY_ON_TRACK_2_QUESTION)}</StepTitle>
                <StepDescription>{translate(STRINGS.STAY_ON_TRACK_2_ANSWER)}</StepDescription>
              </StepContent>

              <StepContent>
                <StepTitle>{translate(STRINGS.STAY_ON_TRACK_3_QUESTION)}</StepTitle>
                <StepDescription>{translate(STRINGS.STAY_ON_TRACK_3_ANSWER)}</StepDescription>
              </StepContent>

              <StepContent>
                <StepTitle>{translate(STRINGS.STAY_ON_TRACK_4_QUESTION)}</StepTitle>
                <StepDescription>{translate(STRINGS.STAY_ON_TRACK_4_ANSWER)}</StepDescription>
              </StepContent>
            </SubStepRoot>
          }
          animationDelay={200}
        />

        <Step
          title={translate(STRINGS.THE_MORE_VISITS_YOU_COMPLETE_THE_HIGHER_YOUR_EARNINGS)}
          description={translate(STRINGS.REACH_NEW_RANKS_FOR_EXCITING_REWARDS_AND_RECOGNITION)}
          imgSrc="/assets/3d-graph-up.jpg"
          animationDelay={300}
        />
      </Steps>

      <SafeArea bottom={true}>
        <Footer>
          <FooterTitle>{translate(STRINGS.MAKE_A_DIFFERENCE)}</FooterTitle>
          <FooterDescription>
            {translate(STRINGS.THIS_BENEFIT_WILL_BE_OFFERED_FOR_THE_NEXT_FEW_MONTHS)}
          </FooterDescription>
        </Footer>
      </SafeArea>
    </Scaffold>
  );
}

const Step = (props: {
  title: string;
  description: string;
  imgSrc: string;
  animationDelay?: number;
  direction?: "reverse";
  footer?: React.ReactNode;
}) => {
  return (
    <AppSlideFadeIn delay={props.animationDelay}>
      <StepCard>
        <AppCardContent>
          <StepRoot flexDirection={props.direction}>
            <StepImage src={props.imgSrc} />
            <StepContent>
              <StepTitle>{props.title}</StepTitle>
              <StepDescription>{props.description}</StepDescription>
            </StepContent>
          </StepRoot>
        </AppCardContent>
        {Boolean(props.footer) && <AppCardFooter>{props.footer}</AppCardFooter>}
      </StepCard>
    </AppSlideFadeIn>
  );
};

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 24px;
`;

const FooterTitle = styled.h2`
  color: var(--tw-blue-500);
  font-size: 22px;
  font-weight: 800;
  margin: 0;
  text-align: center;
`;

const FooterDescription = styled.p`
  color: var(--tw-slate-600);
  font-size: 16px;
  margin: 0;
  text-align: center;
`;

const StepImage = styled.img`
  width: 82px;
  height: 82px;
  border-radius: 8px;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
`;

const StepTitle = styled.h3`
  font-size: 16px;
  margin: 0;
`;

const StepDescription = styled.p`
  font-size: 14px;
  color: var(--tw-slate-600);
  margin: 0;
`;

const StepCard = styled(AppCard)``;

const SubStepRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${StepContent} {
    gap: 0;
  }

  ${StepTitle} {
    font-size: 14px;
  }

  ${StepDescription} {
    font-size: 12px;
  }
`;

const StepRoot = styled.div<{ flexDirection?: "reverse" }>`
  display: flex;
  gap: 16px;
  flex-direction: ${(props) => (props.flexDirection === "reverse" ? "row-reverse" : "row")};
`;

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 20px;
`;
